import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import {
  ICD10_BLOCK_LEVEL,
  ICD10_CATEGORY_LEVEL,
  Icd10BlockGroup,
  groupIcd10Codes,
  icd10CategoryPrefixes,
  icd10DisplayName,
  matchIcd10Codes,
} from '@lib/insurance-packages/data-access';
import { Icd10DiagnosesTreeComponent } from '@lib/insurance-packages/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, filter, forkJoin } from 'rxjs';

import { MatProgressSpinner } from '@angular/material/progress-spinner';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;
const ICD10_SEARCH_PAGE_SIZE = 500;
const ICD10_BLOCKS_KEY = 'icd10-blocks';

interface DiagnosisNamesState {
  names: Record<string, string>;
  loading: boolean;
  error: boolean;
}

interface DiagnosesSearchState {
  results: Record<string, string>;
  loading: boolean;
  error: boolean;
}

interface DiagnosisSearchHit {
  code: string;
  name: string;
}

@Component({
  selector: 'tasks-member-coverage-diagnoses',
  standalone: true,
  imports: [MatProgressSpinner, TranslatePipe, Icd10DiagnosesTreeComponent],
  templateUrl: './member-coverage-diagnoses.component.html',
  host: { class: 'flex min-h-0 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberCoverageDiagnosesComponent implements OnInit {
  private readonly dictionariesApiService = inject(DictionariesApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly codes = input.required<string[]>();
  readonly search = input<string>('');

  protected readonly diagnosisNames = signal<Record<string, DiagnosisNamesState>>({});
  private readonly diagnosesSearch = signal<Record<string, DiagnosesSearchState>>({});
  protected readonly allNames = computed<Record<string, string>>(() => Object.assign({}, ...Object.values(this.diagnosisNames()).map(state => state.names)));

  protected readonly blocksState = computed(() => this.diagnosisNames()[ICD10_BLOCKS_KEY]);
  protected readonly groups = computed<Icd10BlockGroup[]>(() => groupIcd10Codes(this.codes(), Object.keys(this.blocksState()?.names ?? {})));

  private readonly searchTerm = computed<string>(() => this.search().trim());
  protected readonly searching = computed<boolean>(() => this.searchTerm().length >= SEARCH_MIN_LENGTH);
  protected readonly searchState = computed(() => this.diagnosesSearch()[this.searchTerm()] ?? null);
  protected readonly searchHits = computed<DiagnosisSearchHit[]>(() => {
    const results = this.searchState()?.results ?? {};
    const names = this.allNames();

    return matchIcd10Codes(this.codes(), this.searchTerm(), results).map(code => ({ code, name: results[code] || names[code] || '' }));
  });

  constructor() {
    toObservable(this.searchTerm)
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        filter(term => term.length >= SEARCH_MIN_LENGTH && this.needsSearch(term)),
        takeUntilDestroyed()
      )
      .subscribe(term => this.searchDiagnoses(term));
  }

  ngOnInit(): void {
    if (this.needsLoading(ICD10_BLOCKS_KEY)) this.loadBlocks();
  }

  protected onBlockOpened(group: Icd10BlockGroup): void {
    if (this.needsLoading(group.key)) this.loadBlockNames(group);
  }

  protected onCategoryOpened(category: string): void {
    if (this.needsLoading(category)) this.loadCategoryNames(category);
  }

  protected retryNames(key: string): void {
    const group = this.groups().find(item => item.key === key);
    if (group) this.loadBlockNames(group);
    else this.loadCategoryNames(key);
  }

  protected retrySearch(): void {
    this.searchDiagnoses(this.searchTerm());
  }

  protected loadBlocks(): void {
    this.loadNames(ICD10_BLOCKS_KEY, [''], ICD10_BLOCK_LEVEL);
  }

  private loadBlockNames(group: Icd10BlockGroup): void {
    this.loadNames(group.key, icd10CategoryPrefixes(group.categories.map(item => item.category)), ICD10_CATEGORY_LEVEL);
  }

  private loadCategoryNames(category: string): void {
    this.loadNames(category, [category]);
  }

  private loadNames(key: string, searchTerms: string[], level?: number): void {
    this.setNames(key, { names: {}, loading: true, error: false });

    forkJoin(
      searchTerms.map(searchTerm => this.dictionariesApiService.searchIcd10Diagnoses({ searchTerm, level, pageSize: ICD10_SEARCH_PAGE_SIZE, sortBy: 'code', sortDirection: 'Asc' }))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: responses => {
          const items = responses.flatMap(response => response.items);
          this.setNames(key, { names: Object.fromEntries(items.map(item => [item.code, icd10DisplayName(item)])), loading: false, error: false });
        },
        error: () => this.setNames(key, { names: {}, loading: false, error: true }),
      });
  }

  private searchDiagnoses(term: string): void {
    this.setSearch(term, { results: {}, loading: true, error: false });

    this.dictionariesApiService
      .searchIcd10Diagnoses({ searchTerm: term, pageSize: ICD10_SEARCH_PAGE_SIZE, sortBy: 'code', sortDirection: 'Asc' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.setSearch(term, { results: Object.fromEntries(response.items.map(item => [item.code, icd10DisplayName(item)])), loading: false, error: false }),
        error: () => this.setSearch(term, { results: {}, loading: false, error: true }),
      });
  }

  private setNames(key: string, state: DiagnosisNamesState): void {
    this.diagnosisNames.update(current => ({ ...current, [key]: state }));
  }

  private setSearch(term: string, state: DiagnosesSearchState): void {
    this.diagnosesSearch.update(current => ({ ...current, [term]: state }));
  }

  private needsSearch(term: string): boolean {
    const state = this.diagnosesSearch()[term];
    return !state || state.error;
  }

  private needsLoading(key: string): boolean {
    const state = this.diagnosisNames()[key];
    return !state || state.error;
  }
}
